<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import PageHeader from '@/components/PageHeader.vue'
import { encodeWebp } from '@/lib/trackEditor/tileCut'
import { exportImageTiles } from '@/lib/trackEditor/imageTileExport'
import { downloadBlob, nextFrame } from '@/lib/browser'
import { makeZip } from '@/lib/zip'

const trackInput = ref(null)
const pictureInput = ref(null)

/** What a picture's own container says, for a file that arrives without an extension. */
const PICTURE_EXTENSIONS = {
  'image/png': 'png',
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/avif': 'avif',
  'image/gif': 'gif',
}

/** The track file as loaded: the picture alone would say nothing about the map it covers. */
const track = ref(null)
/** `{ bitmap, name, extension, bytes, url }`: the picture decoded, and the very file the zip carries. */
const picture = ref(null)

const error = ref('')
const status = ref('')
/** What the export is busy with, or null (the button stays disabled meanwhile). */
const exporting = ref(null)

const ready = computed(() => Boolean(track.value && picture.value) && !exporting.value)
/** The picture's name in the game's folder: the track's own id, whatever the file was called. */
const pictureFile = computed(() => (track.value && picture.value
  ? `${track.value.id ?? 'track_custom'}.${picture.value.extension}`
  : ''))
const trackSummary = computed(() => (track.value
  ? `${track.value.id ?? 'track_custom'} — ${track.value.width} × ${track.value.height}`
  : 'No JSON loaded'))
const pictureSummary = computed(() => (picture.value
  ? `${picture.value.name} — ${picture.value.bitmap.width} × ${picture.value.bitmap.height} px`
  : 'No picture loaded'))

async function onTrackFile(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  error.value = ''
  status.value = ''
  try {
    const data = JSON.parse(await file.text())
    if (!(data?.width > 0) || !(data?.height > 0)) throw new Error('no size')
    track.value = data
  } catch {
    track.value = null
    error.value = 'This JSON is not a track: its width or height is missing.'
  }
}

async function onPictureFile(event) {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  error.value = ''
  status.value = ''
  try {
    const bitmap = await createImageBitmap(file)
    releasePicture()
    picture.value = {
      bitmap,
      name: file.name,
      extension: extensionOf(file),
      bytes: new Uint8Array(await file.arrayBuffer()),
      url: URL.createObjectURL(file),
    }
  } catch {
    error.value = 'This picture cannot be read.'
  }
}

/**
 * Writes the package the game reads: the track's own JSON (geometry untouched, its stamp preserved),
 * the picture as it was loaded, and the tile pyramid cut from it — the same one the editor paints.
 */
async function exportTiles() {
  if (!ready.value) return
  exporting.value = 'Preparing the export…'
  error.value = ''
  status.value = ''
  try {
    await nextFrame()
    const tiles = await exportImageTiles(picture.value.bitmap, track.value, {
      encode: encodeWebp,
      onProgress: async ({ level, index, total }) => {
        exporting.value = `Cutting the tiles: row ${index + 1} of ${total} (level ${level})…`
        await nextFrame()
      },
    })
    if (!tiles) {
      error.value = 'Nothing to export: the track has no size.'
      return
    }

    exporting.value = 'Packing the archive…'
    await nextFrame()
    const id = track.value.id ?? 'track_custom'
    const data = {
      ...track.value,
      backgroundImage: pictureFile.value,
      backgroundTiles: tiles.manifest,
    }
    const zip = makeZip([
      { path: `assets/tracks/${id}.json`, data: encodeJson(data) },
      { path: `assets/img/tracks/${pictureFile.value}`, data: picture.value.bytes },
      ...tiles.files.map((file) => ({ path: `assets/img/tracks/${file.path}`, data: file.data })),
    ])
    downloadBlob(`${id}-tiles.zip`, zip)
    status.value = `Exported ${tiles.tileCount} tiles for "${id}" `
      + `(${(zip.size / (1024 * 1024)).toFixed(1)} MB): unzip it into the game's src folder.`
  } catch (err) {
    console.error(err)
    error.value = err?.message ?? String(err)
  } finally {
    exporting.value = null
  }
}

const encodeJson = (data) => new TextEncoder().encode(JSON.stringify(data, null, 2))

/** The extension the picture is written with: its own, else the one the browser read off the file. */
function extensionOf(file) {
  const extension = (/\.([a-z0-9]+)$/i.exec(file.name)?.[1] ?? PICTURE_EXTENSIONS[file.type] ?? 'png').toLowerCase()
  // A JPEG is written as `.jpg`, one spelling only.
  return extension === 'jpeg' || extension === 'jpe' ? 'jpg' : extension
}

function releasePicture() {
  if (!picture.value) return
  picture.value.bitmap.close?.()
  URL.revokeObjectURL(picture.value.url)
  picture.value = null
}

onBeforeUnmount(releasePicture)
</script>

<template>
  <v-container>
    <PageHeader title="Post production" icon="mdi-layers-triple-outline" />

    <v-alert type="info" variant="tonal" density="compact" class="mb-6">
      Load the track's JSON and the picture the editor exported, edited however you like:
      you get the same tiles the editor writes, ready to drop into the game's folder.
    </v-alert>

    <v-row>
      <v-col cols="12" md="6">
        <v-card elevation="2" class="h-100">
          <v-card-title>Track (JSON)</v-card-title>
          <v-card-text>
            <v-btn variant="tonal" prepend-icon="mdi-file-code-outline" @click="trackInput.click()">
              Choose the JSON
            </v-btn>
            <input ref="trackInput" type="file" accept=".json,application/json" class="d-none" @change="onTrackFile">
            <p class="text-body-2 text-medium-emphasis mt-3 mb-0">{{ trackSummary }}</p>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card elevation="2" class="h-100">
          <v-card-title>Track picture</v-card-title>
          <v-card-text>
            <v-btn variant="tonal" prepend-icon="mdi-image-outline" @click="pictureInput.click()">
              Choose the picture
            </v-btn>
            <input ref="pictureInput" type="file" accept="image/*" class="d-none" @change="onPictureFile">
            <p class="text-body-2 text-medium-emphasis mt-3 mb-0">{{ pictureSummary }}</p>
            <p v-if="pictureFile" class="text-caption text-medium-emphasis mb-0">
              In the game's folder: {{ pictureFile }}
            </p>
            <v-img
              v-if="picture"
              :src="picture.url"
              :alt="picture.name"
              max-height="220"
              contain
              class="mt-3 bg-checkerboard"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div class="d-flex align-center ga-4 mt-6">
      <v-btn color="primary" prepend-icon="mdi-grid" :disabled="!ready" @click="exportTiles">
        Export the tiles
      </v-btn>
      <span v-if="exporting" class="text-body-2">{{ exporting }}</span>
    </div>

    <v-progress-linear v-if="exporting" indeterminate color="primary" class="mt-4" />

    <v-alert v-if="error" type="error" variant="tonal" class="mt-6">{{ error }}</v-alert>
    <v-alert v-if="status" type="success" variant="tonal" class="mt-6">{{ status }}</v-alert>
  </v-container>
</template>

<style scoped>
/* Transparent areas of the picture are the map's edges: they read better over a light checkerboard. */
.bg-checkerboard {
  background-image:
    linear-gradient(45deg, #dcdcdc 25%, transparent 25%, transparent 75%, #dcdcdc 75%),
    linear-gradient(45deg, #dcdcdc 25%, #ffffff 25%, #ffffff 75%, #dcdcdc 75%);
  background-size: 16px 16px;
  background-position: 0 0, 8px 8px;
}
</style>
